import type { ReportType } from '@/components/WellLoggingOperations/types/filterReport';
import type { CascaderProps } from 'antd';
import { reportFilters, reportForms } from '@/constants/reports';

/*
    Список опций типов отчетов для компонента - Каскадер.
    Пока управляется в ручную.

    Возможно, потом эта информация уйдет на сервер.
*/

/*
    Поле form - характерно для отчетов BASE группы.
    За каждым типом отчета может быть своя форма.

    Все виды форм представлены в файле @/constants/reports в константе reportForms.

    Пояснения:
    - если тип формы не указан, будет выбрана базовая форма (BASE)
    - если форма не нужна, нужно указать тип (EMPTY) (TODO: пока не работает)

    Там где в поле values сейчас стоят ЧИСЛА - значит НЕ БЫЛ НАЙДЕН соответствующий отчет.
*/

/*
    Поле filter - нестандартное поле. Переключает интерфейс фильтра.
    Проставляется только в тех отчетах, где это необходимо.

    При простановке этого поля свойство form инициализируется как EMPTY.
*/

type AntdCascaderOption = Required<CascaderProps>['options'][number];

export type ReportOption = Omit<AntdCascaderOption, 'value' | 'children'> & {
    value: ReportType;
    children?: ReportOption[];
};

export const DEFAULT_REPORT_TYPE: ReportType[] = ['TechMode', 'ProducingWells'];

export const cascaderOptions: ReportOption[] = [
    {
        value: 'Mer',
        label: 'МЭР',
        children: [
            { value: 'OilWells', label: 'Нефтяные', form: reportForms.MER, filter: reportFilters.MER },
            {
                value: 'GasWells',
                label: 'Газовые',
                filter: reportFilters.MER,
                children: [
                    {
                        value: 'GasWells',
                        label: 'Газовые',
                        form: reportForms.MER,
                        filter: reportFilters.MER,
                    },
                    {
                        value: 'GasCapWells',
                        label: 'Нефтяные с газом из газовой шапки',
                        form: reportForms.MER,
                        filter: reportFilters.MER,
                    },
                    {
                        value: 'GasCondensateWells',
                        label: 'Газоконденсатные',
                        form: reportForms.MER,
                        filter: reportFilters.MER,
                    },
                ],
            },
            {
                value: 'InjectionWells',
                label: 'Нагнетательные',
                form: reportForms.MER,
                filter: reportFilters.MER,
            },
            {
                value: 'AbsorbingWells',
                label: 'Поглощающие',
                form: reportForms.MER,
                filter: reportFilters.MER,
            },
            { value: 'WaterWells', label: 'Водозаборные', form: reportForms.MER, filter: reportFilters.MER },
        ],
    },
    {
        value: 'TechMode',
        label: 'Технологические режимы',
        children: [
            { value: 'ProducingWellsMer', label: 'Добывающие МЭР', form: reportForms.BASE_GROUPING },
            { value: 'ProducingWells', label: 'Добывающие', form: reportForms.BASE_GROUPING },
            {
                value: 'GasWells',
                label: 'Газовые',
                children: [
                    {
                        value: 'GasWells',
                        label: 'Газовые',
                        form: reportForms.BASE_GROUPING,
                    },
                    {
                        value: 'GasCondensateWells',
                        label: 'Газоконденсатные',
                        form: reportForms.BASE_GROUPING,
                    },
                ],
            },
            { value: 'InjectionWells', label: 'Нагнетательные', form: reportForms.BASE_GROUPING },
            {
                value: 'DataCompletenessCorrectness',
                label: 'Полнота и корректность данных',
                form: reportForms.COMPLETENESS,
            },
            { value: 'AbsorbingWells', label: 'Поглощающие', form: reportForms.BASE_GROUPING },
            { value: 'WaterWells', label: 'Водозаборные', form: reportForms.BASE_GROUPING },
        ],
    },
    {
        value: 'OperCard',
        label: 'Эксплуатационные карточки',
        children: [
            { value: 'ProducingWells', label: 'Добывающие' },
            { value: 'ProducingWellsBoard', label: 'Добывающие (пульт)' },
            {
                value: 'GasWells',
                label: 'Газовые',
                children: [
                    {
                        value: 'GasWells',
                        label: 'Газовые',
                    },
                    {
                        value: 'GasCondensateWells',
                        label: 'Газоконденсатные',
                    },
                ],
            },
            { value: 'InjectionWells', label: 'Нагнетательные' },
            { value: 'AbsorbingWells', label: 'Поглощающие' },
            { value: 'WaterWells', label: 'Водозаборные' },
        ],
    },
    {
        value: 'WellStock',
        label: 'Фонд скважин',
        children: [
            { value: 'Distribution', label: 'Распределение фонда', form: reportForms.BASE_GROUPING },
            {
                value: 'ExtendedDistribution',
                label: 'Расширенное распределение фонда',
                form: reportForms.BASE_GROUPING,
            },
            { value: 'State', label: 'Состояние фонда скважин', form: reportForms.BASE_GROUPING },
            { value: 'OilWells', label: 'Фонд нефтяных скважин', form: reportForms.SHORT_BASE_GROUPING },
            {
                value: 'GasWells',
                label: 'Фонд газовых скважин',
                children: [
                    {
                        value: 'GasWells',
                        label: 'Фонд газовых скважин',
                        form: reportForms.SHORT_BASE_GROUPING,
                    },
                    {
                        value: 'GasCondensateWells',
                        label: 'Фонд газоконденсатных скважин',
                        form: reportForms.SHORT_BASE_GROUPING,
                    },
                ],
            },
            { value: 'InjectionWell', label: 'Фонд нагнетательных скважин', form: reportForms.SHORT_BASE_GROUPING },
            {
                value: 'Nonworking',
                label: 'Неработающий фонд нефтяных скважин',
                form: reportForms.SHORT_BASE,
            },
        ],
    },
    {
        value: 'Other',
        label: 'Другие',
        children: [
            { value: 'StratumReport', label: 'Отчет по пластам' },
            {
                value: 'EfficiencyIndicators',
                label: 'Показатели технологической эффективности эксплуатации скважин различной конструкции («ПТЭЭСРК»)',
            },
            { value: 'MeanProduction', label: 'Средняя добыча по участкам' },
            { value: 'Pokachi1', label: 'Покачи 1' },
            { value: 'Pokachi1Sum', label: 'Покачи 1 (Сумма по скважине)' },
            { value: 'Pokachi2', label: 'Покачи 2' },
            { value: 'Testing', label: 'Опробования', form: reportForms.SAMPLING_GKZ },
            { value: 'TestingGKZ', label: 'Опробования ГКЗ', form: reportForms.SAMPLING_GKZ },
        ],
    },
    {
        value: 'Rigis',
        label: 'РИГИС + репера',
        children: [
            { value: 'Rigis', label: 'Выгрузка РИГИС', disabled: true },
            { value: 'MeanParameters', label: 'Осредненные параметры', disabled: true },
            { value: 'ReferencePoints', label: 'Отчет по реперам', disabled: true },
            { value: 'MeanParametersByReferencePoints', label: 'Осредненные параметры по реперам', disabled: true },
        ],
    },
    {
        value: 'Gtm',
        label: 'ГТМ',
        children: [
            { value: 'GtmSuccess', label: 'Оценка успешности ГТМ', form: reportForms.BASE_GROUPING },
            { value: 'GtmFact', label: 'Факт выполнения ГТМ', form: reportForms.BASE_GROUPING },
            {
                value: 'HydraulicFracturingMainInformation',
                label: 'ГРП (общие сведения)',
            },
            {
                value: 'HydraulicFracturingByStages',
                label: 'ГРП (Информация по стадиям)',
            },
            {
                value: 'WellProcessingActivities',
                label: 'Мероприятиях по обработке скважин',
            },
            {
                value: 'Repair',
                label: 'Расширенная информация ПРС/КРС (банк данных)',
                children: [
                    {
                        value: 'CapitalRepairMainData',
                        label: 'Основные данные по капитальному ремонту скважин',
                    },
                    {
                        value: 'CapitalRepairSecondHole',
                        label: 'Данные по капитальному ремонту скважин с зарезкой второго ствола',
                    },
                    {
                        value: 'CapitalRepairRirType',
                        label: 'Данные по капитальному ремонту скважин типа РИР',
                    },
                    {
                        value: 'BottomHoleZone',
                        label: 'Данные об обработки призабойной зоны (Ремонт КРС ОПЗ)',
                    },
                    {
                        value: 'CapitalRepairHydraulicFracturing',
                        label: 'Данные по капитальному ремонту скважин с гидроразрывом пласта(ГРП)',
                    },
                    {
                        value: 'UndergroundRepair',
                        label: 'Основные данные по подземному ремонту скважин',
                    },
                    { value: 'Packer', label: 'Данные о пакерах' },
                ],
            },
            {
                value: 'Main',
                label: 'По основным ГТМ',
                children: [
                    { value: 'MainGtm', label: 'Основные ГТМ' },
                    { value: 'SideHoles', label: 'Боковые стволы' },
                    { value: 'Returns', label: 'Возвраты' },
                    { value: 'NewWells', label: 'Новые скважины' },
                    { value: 'HydraulicFracturing', label: 'ГРП' },
                    { value: 'MainGtmResults', label: 'Итоги основных ГТМ' },
                ],
            },
            { value: 'ActivitySelection', label: 'Подбор вида мероприятия' },
        ],
    },
    {
        value: 'GDI',
        label: 'Гидродинамические исследования',
        form: reportForms.EMPTY,
        filter: reportFilters.GDI,
    },
    {
        value: 'Forms',
        label: 'Формы',
        children: [
            {
                value: 'GosPlanXML',
                label: 'Госплан',
                form: reportForms.EMPTY,
                filter: reportFilters.GOSPLAN,
            },
            {
                value: 'PlanOrder',
                label: 'План-заказ на ремонт',
                isWellOnly: true,
                filter: reportFilters.PLAN_ORDER,
            },
            {
                value: 'EquipmentSelection',
                label: 'Шаблон подбора оборудования',
                isWellOnly: true,
                filter: reportFilters.EQUIPMENT_SELECTION,
            },
            {
                value: 'KRS',
                label: 'Сводка КРС и ГТМ',
                isWellOnly: true,
                filter: reportFilters.KRS_AND_GTM,
            },
        ],
    },
    {
        value: 'RequestsBlock',
        label: 'Блок запросов',
        children: [
            { value: 'Drilling', label: 'Бурение', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'GISDrillingConclusion', label: 'ГИС – бурение заключение', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'GISDrillingCurves', label: 'ГИС – бурение кривые', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'GISControlConclusion', label: 'ГИС – контроль заключение', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'GISControlCurves', label: 'ГИС – контроль кривые', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'GRP', label: 'ГРП', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'GTM', label: 'ГТМ', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'ReservoirPressures', label: 'Давления пластовые', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Measurements', label: 'Замеры', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Isolation', label: 'Изоляция', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Inclinometry', label: 'Инклинометрия', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'DrillingHistory', label: 'История добычи', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Core', label: 'Керн', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Conservation', label: 'Консервация', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Construction', label: 'Конструкция', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Coordinates', label: 'Координаты', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Liquidation', label: 'Ликвидация', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Modeling', label: 'Моделирование', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Testing', label: 'Опробывание', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Development', label: 'Освоение', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'ReportsNIPI', label: 'Отчёты НИПИ', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Perforation', label: 'Перфорация', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'UndergroundEquipment', label: 'Подземное оборудование', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Potentials', label: 'Потенциалы', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'WellBreakdown', label: 'Разбивки по скважинам', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Registry', label: 'Реестр', filter: reportFilters.REQUESTS_BLOCK },
            {
                value: 'GDISResults',
                label: 'Результаты ГДИС',
                filter: reportFilters.REQUESTS_BLOCK,
                // В компоненте handleChange у этой пункта идет редирект на данные
                //  {
                // value: 'GDI',
                // label: 'Гидродинамические исследования',
                // form: reportForms.EMPTY,
                // filter: reportFilters.GDI,
                //  },
            },
            { value: 'Repairs', label: 'Ремонты', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Reference', label: 'Справочники', filter: reportFilters.REQUESTS_BLOCK },
            { value: 'Pressure', label: 'Давления пластовые', filter: reportFilters.REQUESTS_BLOCK },
            {
                value: 'StratigraphicBreakdown',
                label: 'Стратиграфические разбивки',
                filter: reportFilters.REQUESTS_BLOCK,
            },
            {
                value: 'TechnicalInformationOnWells',
                label: 'Техническая информация по скважинам',
                filter: reportFilters.REQUESTS_BLOCK,
            },
            { value: 'Fluid', label: 'Флюид', filter: reportFilters.REQUESTS_BLOCK },
        ],
    },
    {
        value: '',
        label: 'Нерегламентные отчёты',
        disabled: true,
    },
    {
        value: 'WellResearch',
        label: 'Исследования скважин',
        filter: reportFilters.WELL_RESEARCH,
    },
    {
        value: 'TechInfo',
        label: 'Техническая информация',
        filter: reportFilters.TECH_INFO,
    },
    {
        value: 'GDS',
        label: 'Выгрузка данных для ГДС',
        form: reportForms.GDS,
        filter: reportFilters.GDS,
    },
    {
        value: 'ESRI',
        label: 'Выгрузка геоданных в формате ESRI',
        form: reportForms.ESRI,
        filter: reportFilters.ESRI,
    },
];
